import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Switch,
  Spinner,
  Grid,
} from '@chakra-ui/react'
import {
  createEmailTemplateSchema,
  updateEmailTemplateSchema,
  type CreateEmailTemplateFormData,
  type UpdateEmailTemplateFormData,
} from '@/lib/validations/auth'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'

interface EmailTemplateFormProps {
  emailTemplateId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function EmailTemplateForm({ emailTemplateId, onSuccess, onCancel }: EmailTemplateFormProps) {
  const colors = useThemeColors()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(!!emailTemplateId)
  const isEditMode = !!emailTemplateId

  const { showError, showSuccess } = useToastNotification()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateEmailTemplateFormData | UpdateEmailTemplateFormData>({
    resolver: zodResolver(isEditMode ? updateEmailTemplateSchema : createEmailTemplateSchema),
    defaultValues: {
      name: '',
      type: '',
      receiver: '',
      cc: '',
      subject: '',
      body: '',
      description: '',
      is_active: true,
    },
  })

  const isActive = watch('is_active')

  // Fetch email template data if editing
  useEffect(() => {
    if (emailTemplateId) {
      const fetchEmailTemplate = async () => {
        try {
          setIsLoading(true)
          const response = await apiClient.get(`/system-settings/email-template/${emailTemplateId}`)
          const data = response.data?.data || response.data

          if (data) {
            reset({
              name: data.name || '',
              type: data.type || '',
              receiver: data.receiver || '',
              cc: data.cc || '',
              subject: data.subject || '',
              body: data.body || '',
              description: data.description || '',
              is_active: data.is_active ?? true,
            })
          }
        } catch (error: any) {
          showError(error)
        } finally {
          setIsLoading(false)
        }
      }

      fetchEmailTemplate()
    }
  }, [emailTemplateId, reset, showError])

  const onSubmit = async (data: CreateEmailTemplateFormData | UpdateEmailTemplateFormData) => {
    try {
      setIsSubmitting(true)

      if (isEditMode && emailTemplateId) {
        await apiClient.put(`/system-settings/email-template/${emailTemplateId}`, data)
        showSuccess('Success', 'Email template updated successfully')
      } else {
        await apiClient.post('/system-settings/email-template', data)
        showSuccess('Success', 'Email template created successfully')
      }

      onSuccess?.()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4}>Loading email template...</Text>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Template Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.name}>
            <Input {...register('name')} placeholder="e.g., Welcome Email" />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Template Type <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.type}>
            <Input
              {...register('type')}
              placeholder="e.g., welcome_email"
              isDisabled={isEditMode}
            />
            <FormHelperText>
              Unique identifier for this template (cannot be changed after creation)
            </FormHelperText>
            <FormErrorMessage>{errors.type?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Default Receiver Email
          </FormLabel>
          <FormControl isInvalid={!!errors.receiver}>
            <Input {...register('receiver')} type="email" placeholder="default@example.com" />
            <FormHelperText>
              Default recipient email address (can be overridden when sending)
            </FormHelperText>
            <FormErrorMessage>{errors.receiver?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            CC Email Addresses
          </FormLabel>
          <FormControl isInvalid={!!errors.cc}>
            <Input {...register('cc')} placeholder="cc1@example.com, cc2@example.com" />
            <FormHelperText>
              Comma-separated list of CC email addresses
            </FormHelperText>
            <FormErrorMessage>{errors.cc?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Email Subject <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.subject}>
            <Input {...register('subject')} placeholder="e.g., Welcome to [company_name]" />
            <FormHelperText>
              Use placeholders like [recipient_name], [request_number], etc.
            </FormHelperText>
            <FormErrorMessage>{errors.subject?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Description
          </FormLabel>
          <FormControl isInvalid={!!errors.description}>
            <Textarea
              {...register('description')}
              placeholder="Brief description of when this template is used"
              rows={3}
            />
            <FormHelperText>
              Optional description explaining when this template is used
            </FormHelperText>
            <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Email Body (HTML) <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.body}>
            <Textarea
              {...register('body')}
              placeholder="<html>...</html>"
              rows={15}
              fontFamily="mono"
              fontSize="sm"
            />
            <FormHelperText>
              HTML content. Use placeholders like [recipient_name], [request_number], [link], etc.
            </FormHelperText>
            <FormErrorMessage>{errors.body?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Active
          </FormLabel>
          <FormControl>
            <HStack>
              <Switch
                isChecked={isActive}
                onChange={(e) => setValue('is_active', e.target.checked)}
                colorScheme="green"
              />
              <Text fontSize="sm" color={colors.text}>
                {isActive ? 'Active' : 'Inactive'}
              </Text>
            </HStack>
            <FormHelperText>
              Active templates will be used when sending emails
            </FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Template'}
            {isSubmitting && (
              <Text as="span" ml={2} fontSize="sm">
                ...
              </Text>
            )}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
